'use client'

import { useMemo } from 'react'

import { AlternateModelVariantAction } from '../../../types/graphql/site-fixtures'
import { usePersonalization } from '../../_providers/PersonalizationProvider.client'

import type { NavAccordionSectionFragmentFragment } from '../../../__generated__/graphql/catalog/graphql'

type NavAccordionSection = NavAccordionSectionFragmentFragment['sections'][number]

type ValidateTargetCriteria = ReturnType<
  typeof usePersonalization
>['validatePersonalizationTargetCriteria']

/**
 * Alternate Model Variant also supports a "replace original" action but it is not implemented here.
 * The query complexity for GetSiteFixtures exceeds the maximum limit when including the
 * validContentfulModel field for accordion sections, so only "hide original" is supported.
 */
export const isNavAccordionSectionHidden = (
  section: Pick<NavAccordionSection, 'alternateModelVariant'>,
  validatePersonalizationTargetCriteria: ValidateTargetCriteria
): boolean => {
  const variant = section.alternateModelVariant
  if (!variant?.targetCriteria) return false
  if (variant.action !== AlternateModelVariantAction.hideOriginal) return false
  return validatePersonalizationTargetCriteria(variant.targetCriteria)
}

export const useVisibleNavAccordionSections = <
  T extends Pick<NavAccordionSection, 'alternateModelVariant'>,
>(
  sections: ReadonlyArray<T>
): T[] => {
  const { validatePersonalizationTargetCriteria } = usePersonalization()

  return useMemo(
    () =>
      sections.filter(
        section => !isNavAccordionSectionHidden(section, validatePersonalizationTargetCriteria)
      ),
    [sections, validatePersonalizationTargetCriteria]
  )
}
